import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertTriangle,
  CheckCircle,
  Copy,
  Loader2,
  Mail,
  MessageCircle,
  X,
  Users,
  UserPlus,
  User,
  Search,
  Check,
} from "lucide-react";
import { SubmitTicketSkeleton } from "../../components/LoadingSkeleton";
import { useNavigate } from "@tanstack/react-router";
import {
  useCreateCustomerTicket,
  useCustomerPortalCategories,
  useCustomerPortalLookups,
  useCustomerPortalSuggestedArticles,
} from "../../hooks/useCustomerPortal";
import { useCustomerPortalEligibleParticipantsForNewTicket, isCompleteEmailAddress } from "../../hooks/useCustomerPortalParticipants";
import { useCustomerPortalCollections, useCustomerPortalRelatedArticles, portalArticleParam } from "../../hooks/useKnowledgeBase";
import { useDebounce } from "../../hooks/useDebounce";
import { TreeSelect } from "../../components/tree-select/TreeSelect";
import { portalCategoryToOption } from "../../components/tree-select/treeOptions";
import { FileUpload } from "../../components/FileUpload";
import { showToast } from "../../lib/toast";
import type { OrganizationInfo } from "../../lib/portal-loaders";
import type { EligibleParticipantResponse } from "../../types/tickets";
import { usePortalBranding } from "../../hooks/usePortalBranding";

interface PublicTicketSubmissionProps {
  organizationInfo?: OrganizationInfo;
  organizationId?: string | null;
}

interface FormData {
  subject: string;
  description: string;
  priority: number; // Changed to number to match Priority enum values
  categoryId?: string;
}

interface UploadedFile {
  file: File;
  id: string;
}

export const PublicTicketSubmission: React.FC<PublicTicketSubmissionProps> = ({
  organizationInfo,
}) => {
  const navigate = useNavigate();
  const { getContrastText, getThemedPrimaryColor, getThemedSecondaryColor, getThemedPrimaryWithOpacity } =
    usePortalBranding();

  // Determine URL pattern and construct URLs
  const isPathBased = window.location.pathname.startsWith("/org/");
  const getPortalUrl = (path: string) => {
    if (isPathBased && organizationInfo) {
      return `/org/${organizationInfo.subdomain}/portal${path}`;
    }
    return `/portal${path}`;
  };

  // Customer ticket submission - requires authentication
  const createTicketMutation = useCreateCustomerTicket();
  const { data: categories, isLoading: categoriesLoading } =
    useCustomerPortalCategories();
  const { data: lookups, isLoading: lookupsLoading } =
    useCustomerPortalLookups();
  const { data: kbCollections, isLoading: kbCategoriesLoading } = useCustomerPortalCollections();

  // Check if knowledge base has any articles (uncategorized ones count too)
  const hasKnowledgeBaseContent = (kbCollections?.totalArticleCount ?? 0) > 0;

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedTicket, setSubmittedTicket] = useState<{
    ticketId: string;
    ticketNumber: number;
    subject: string;
  } | null>(null);

  const [formData, setFormData] = useState<FormData>({
    subject: "",
    description: "",
    priority: 2, // Medium priority (enum value)
    categoryId: "",
  });

  const [attachments, setAttachments] = useState<UploadedFile[]>([]);

  // Deflection: articles related to the subject as it's typed, then the ones from the collection the chosen category
  // suggests - up to five, each once
  const debouncedSubject = useDebounce(formData.subject, 500);
  const { data: relatedArticles } = useCustomerPortalRelatedArticles(debouncedSubject);
  const { data: categoryArticles } = useCustomerPortalSuggestedArticles(formData.categoryId || undefined);
  const suggestedArticles = (() => {
    const seen = new Set<string>();
    return [...(relatedArticles ?? []), ...(categoryArticles ?? [])]
      .filter((article) => !seen.has(article.id) && seen.add(article.id))
      .slice(0, 5);
  })();
  const selectedCategory = categories?.find((c) => c.id === formData.categoryId);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [selectedParticipants, setSelectedParticipants] = useState<EligibleParticipantResponse[]>([]);
  const [participantSearch, setParticipantSearch] = useState("");
  const [debouncedParticipantSearch, setDebouncedParticipantSearch] = useState("");
  const [isAddParticipantDialogOpen, setIsAddParticipantDialogOpen] = useState(false);
  const [pendingParticipants, setPendingParticipants] = useState<EligibleParticipantResponse[]>([]);

  // Debounce participant search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedParticipantSearch(participantSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [participantSearch]);

  // Search for participants
  const { data: eligibleParticipantsData, isLoading: participantsLoading } =
    useCustomerPortalEligibleParticipantsForNewTicket(
      isAddParticipantDialogOpen ? debouncedParticipantSearch : undefined
    );

  // Filter out already selected participants and pending selections
  const eligibleParticipants = (eligibleParticipantsData || [])
    .filter(c => !selectedParticipants.some(p => p.id === c.id))
    .filter(c => !pendingParticipants.some(p => p.id === c.id));

  const handleToggleParticipantSelection = (customer: EligibleParticipantResponse) => {
    setPendingParticipants(prev => {
      const isSelected = prev.some(c => c.id === customer.id);
      if (isSelected) {
        return prev.filter(c => c.id !== customer.id);
      } else {
        return [...prev, customer];
      }
    });
  };

  const handleAddPendingParticipants = () => {
    if (pendingParticipants.length === 0) return;
    setSelectedParticipants(prev => [...prev, ...pendingParticipants]);
    setPendingParticipants([]);
    setParticipantSearch("");
    setIsAddParticipantDialogOpen(false);
  };

  const handleParticipantDialogOpenChange = (open: boolean) => {
    setIsAddParticipantDialogOpen(open);
    if (!open) {
      setPendingParticipants([]);
      setParticipantSearch("");
    }
  };

  const handleRemoveParticipant = (participantId: string) => {
    setSelectedParticipants(prev => prev.filter(p => p.id !== participantId));
  };

  const handleInputChange =
    (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const handleSelectChange = (field: keyof FormData) => (value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === "priority" ? parseInt(value, 10) : value,
    }));
  };

  const handleFileUpload = (files: File[]) => {
    const newFiles: UploadedFile[] = files.map((file) => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
    }));

    setAttachments((prev) => [...prev, ...newFiles]);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    // Subject validation (match agent portal)
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    } else if (formData.subject.trim().length > 200) {
      newErrors.subject = "Subject must be less than 200 characters";
    }

    // Description validation (match agent portal)
    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    } else if (formData.description.trim().length > 5000) {
      newErrors.description = "Description is too long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Create payload with attachments for atomic submission
      const requestPayload = {
        subject: formData.subject,
        description: formData.description,
        priority: formData.priority, // Send as number (enum value)
        categoryId:
          formData.categoryId && formData.categoryId !== ""
            ? formData.categoryId
            : undefined,
        attachments:
          attachments.length > 0 ? attachments.map((a) => a.file) : undefined,
        participantCustomerIds: selectedParticipants.length > 0
          ? selectedParticipants.map(p => p.id)
          : undefined,
      };

      // Submit using mutation with attachments atomically
      const result = await createTicketMutation.mutateAsync(requestPayload);

      setSubmittedTicket(result);
      setIsSubmitted(true);

      // Show success toast with attachment count if present
      if (attachments.length > 0) {
        showToast.success(
          "Ticket Submitted Successfully!",
          `Your support ticket ${result.ticketNumber} has been created with ${attachments.length} attachment(s).`
        );
      } else {
        showToast.success(
          "Ticket Submitted Successfully!",
          `Your support ticket ${result.ticketNumber} has been created.`
        );
      }
    } catch (error: unknown) {
      console.error("Failed to submit ticket:", error);

      // Show error toast
      showToast.error(
        "Submission Failed",
        (error && typeof error === "object" && "message" in error
          ? (error.message as string)
          : null) ||
          "An error occurred while submitting your ticket. Please try again later."
      );
    }
  };

  const handleGoBack = () => {
    navigate({ to: getPortalUrl("") });
  };

  const handleGoToKnowledgeBase = () => {
    navigate({ to: getPortalUrl("/knowledge-base") });
  };

  const handleGoToMyTickets = () => {
    navigate({ to: getPortalUrl("/tickets") });
  };

  if (isSubmitted && submittedTicket) {
    return (
      <div className="max-w-2xl mx-auto py-6 sm:py-8 px-4">
        <Card>
          <CardContent className="p-6 sm:p-8 text-center">
            <div className="mx-auto mb-4 sm:mb-6 h-12 w-12 sm:h-16 sm:w-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
              <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-green-600 dark:text-green-400" />
            </div>

            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              Ticket Submitted Successfully!
            </h2>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
              <div className="text-xs sm:text-sm space-y-2">
                <div className="flex justify-between items-center gap-2">
                  <span className="text-gray-500 dark:text-gray-400">
                    Ticket Number:
                  </span>
                  <span className="font-mono font-medium text-gray-900 dark:text-white break-all text-right">
                    {submittedTicket.ticketNumber}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">
                    Subject:
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {submittedTicket.subject}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">
                    Status:
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    Open
                  </span>
                </div>
              </div>
            </div>

            <div className="text-sm text-gray-600 dark:text-gray-400 mb-8 space-y-2">
              <p>
                <strong>We've received your support ticket!</strong>
              </p>
              <p>
                Our support team will review your ticket and get back to you
                soon.
              </p>
            </div>

            <div className="flex justify-center">
              <Button
                onClick={handleGoToMyTickets}
                className="transition-all duration-200"
                style={{
                  backgroundColor: getThemedPrimaryColor(),
                  color: getContrastText(getThemedPrimaryColor()),
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                View My Tickets
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading state while KB categories load
  if (kbCategoriesLoading) {
    return <SubmitTicketSkeleton />;
  }

  return (
    <div className="max-w-4xl mx-auto py-6 sm:py-8 space-y-4 sm:space-y-6 px-4">
      {/* Form Content */}
      <div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 items-start">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <Card className="p-4 sm:p-6">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                {/* Page Header */}
                <div className="text-center pb-2">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                    Submit Support Ticket
                  </h1>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
                    Get help from our support team
                  </p>
                </div>

                {/* Ticket Details */}
                <div>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="subject">
                        Subject <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={handleInputChange("subject")}
                        placeholder="Brief description of your issue"
                        disabled={createTicketMutation.isPending}
                        className={errors.subject ? "border-red-500" : ""}
                      />
                      {errors.subject && (
                        <p className="flex items-center gap-1 text-sm text-red-600 mt-1">
                          <AlertTriangle className="h-4 w-4" />
                          {errors.subject}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="priority">
                          Priority <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={formData.priority.toString()}
                          onValueChange={handleSelectChange("priority")}
                          disabled={createTicketMutation.isPending}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {lookups?.priorities.map(
                              (priority: { value: number; label: string }) => (
                                <SelectItem
                                  key={priority.value}
                                  value={priority.value.toString()}
                                >
                                  {priority.label}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="category">Category</Label>
                        <TreeSelect
                          id="category"
                          options={(categories ?? []).map(portalCategoryToOption)}
                          value={formData.categoryId || null}
                          onChange={(id) => handleSelectChange("categoryId")(id ?? "")}
                          placeholder="Select category"
                          noneLabel="Not sure"
                          searchPlaceholder="Search categories..."
                          emptyMessage="No categories"
                          disabled={createTicketMutation.isPending || categoriesLoading}
                        />
                        {selectedCategory?.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">{selectedCategory.description}</p>
                        )}
                      </div>
                    </div>

                    {/* Deflection: articles that might answer the question before it becomes a ticket */}
                    {suggestedArticles && suggestedArticles.length > 0 && organizationInfo?.subdomain && (
                      <div
                        className="rounded-lg border p-3 sm:p-4 space-y-2"
                        style={{ borderColor: getThemedPrimaryWithOpacity(0.3), backgroundColor: getThemedPrimaryWithOpacity(0.05) }}
                        role="region"
                        aria-label="Suggested articles"
                      >
                        <p className="text-sm font-semibold" style={{ color: getThemedPrimaryColor() }}>
                          These articles might help
                        </p>
                        <ul className="space-y-1.5">
                          {suggestedArticles.map((article) => (
                            <li key={article.id}>
                              <a
                                href={`/org/${organizationInfo.subdomain}/portal/knowledge-base/${encodeURIComponent(portalArticleParam(article))}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm hover:underline"
                                style={{ color: getThemedPrimaryColor() }}
                              >
                                {article.title}
                              </a>
                              {article.summary && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1">{article.summary}</p>
                              )}
                            </li>
                          ))}
                        </ul>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Articles open in a new tab, so you won't lose what you've written.
                        </p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="description">
                        Description <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={handleInputChange("description")}
                        placeholder="Please provide detailed information about your issue or ticket..."
                        rows={6}
                        disabled={createTicketMutation.isPending}
                        className={errors.description ? "border-red-500" : ""}
                      />
                      {errors.description && (
                        <p className="flex items-center gap-1 text-sm text-red-600 mt-1">
                          <AlertTriangle className="h-4 w-4" />
                          {errors.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* File Attachments */}
                <div>
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-3 sm:mb-4">
                    Attachments
                  </h3>

                  <FileUpload
                    onFilesChange={handleFileUpload}
                    maxFiles={10}
                    maxSize={10}
                  />
                </div>

                {/* Submit Button */}
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      type="submit"
                      disabled={
                        createTicketMutation.isPending ||
                        categoriesLoading ||
                        lookupsLoading ||
                        kbCategoriesLoading
                      }
                      className="w-full sm:flex-1 transition-all duration-300 hover:scale-105 font-semibold shadow-lg hover:shadow-xl"
                      style={{
                        backgroundColor: getThemedPrimaryColor(),
                        borderColor: getThemedPrimaryColor(),
                        color: getContrastText(getThemedPrimaryColor()),
                        boxShadow: `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}, 0 4px 6px -2px ${getThemedPrimaryWithOpacity(0.2)}`,
                      }}
                      onMouseEnter={(e) => {
                        if (!createTicketMutation.isPending) {
                          e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                          e.currentTarget.style.borderColor = getThemedSecondaryColor();
                          e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                          e.currentTarget.style.boxShadow = `0 25px 50px -12px ${getThemedPrimaryWithOpacity(0.5)}`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = getThemedPrimaryColor();
                        e.currentTarget.style.borderColor = getThemedPrimaryColor();
                        e.currentTarget.style.color = getContrastText(getThemedPrimaryColor());
                        e.currentTarget.style.boxShadow = `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}, 0 4px 6px -2px ${getThemedPrimaryWithOpacity(0.2)}`;
                      }}
                    >
                      {createTicketMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting Ticket...
                        </>
                      ) : (
                        <>
                          <MessageCircle className="mr-2 h-4 w-4" />
                          Submit Support Ticket
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleGoBack}
                      disabled={createTicketMutation.isPending}
                      className="w-full sm:w-auto transition-all duration-200"
                      style={{
                        borderColor: getThemedSecondaryColor(),
                        color: getThemedSecondaryColor(),
                      }}
                      onMouseEnter={(e) => {
                        if (!createTicketMutation.isPending) {
                          e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                          e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = getThemedSecondaryColor();
                      }}
                    >
                      <X className="mr-2 h-4 w-4" />
                      Cancel
                    </Button>
                  </div>
                </div>
              </form>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6 lg:sticky lg:top-6">
            {/* Participants (CC) Section */}
            <Card className="shadow-sm border border-gray-200 dark:border-gray-700 transition-shadow hover:shadow-md">
              <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                    <Users className="h-5 w-5 text-muted-foreground" />
                    Participants
                    {selectedParticipants.length > 0 && (
                      <span className="text-sm font-normal text-muted-foreground">
                        ({selectedParticipants.length})
                      </span>
                    )}
                  </CardTitle>
                  <div className="flex-shrink-0">
                    <Dialog open={isAddParticipantDialogOpen} onOpenChange={handleParticipantDialogOpenChange}>
                      <DialogTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1 transition-all duration-200"
                        style={{
                          borderColor: getThemedSecondaryColor(),
                          color: getThemedSecondaryColor(),
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                          e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = getThemedSecondaryColor();
                        }}
                      >
                        <UserPlus className="h-4 w-4" />
                        <span className="hidden sm:inline">Add</span>
                      </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Add Participant</DialogTitle>
                        <DialogDescription>
                          Add someone to this ticket. They will receive ticket notifications.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        {pendingParticipants.length > 0 && (
                          <div className="flex flex-wrap gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            {pendingParticipants.map((customer) => (
                              <div
                                key={customer.id}
                                className="flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                                style={{
                                  backgroundColor: getThemedPrimaryWithOpacity(0.15),
                                  color: getThemedPrimaryColor(),
                                }}
                              >
                                <span className="truncate max-w-[120px]">{customer.fullName}</span>
                                <button
                                  type="button"
                                  onClick={() => handleToggleParticipantSelection(customer)}
                                  className="rounded-full p-0.5 hover:opacity-70"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            inputMode="email"
                            placeholder="Enter their full email address"
                            value={participantSearch}
                            onChange={(e) => setParticipantSearch(e.target.value)}
                            className="pl-9"
                          />
                        </div>
                        <div className="max-h-64 overflow-y-auto space-y-2">
                          {participantsLoading ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                            </div>
                          ) : eligibleParticipants.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              {isCompleteEmailAddress(participantSearch)
                                ? "No contact found with that email address"
                                : "Enter the full email address of the person you want to add"}
                            </p>
                          ) : (
                            eligibleParticipants.map((customer) => {
                              const isSelected = pendingParticipants.some(c => c.id === customer.id);
                              return (
                                <button
                                  key={customer.id}
                                  type="button"
                                  onClick={() => handleToggleParticipantSelection(customer)}
                                  className="w-full flex items-start gap-3 p-3 rounded-lg transition-colors text-left border"
                                  style={{
                                    backgroundColor: isSelected ? getThemedPrimaryWithOpacity(0.1) : 'transparent',
                                    borderColor: isSelected ? getThemedPrimaryColor() : 'transparent',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.03)';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                  }}
                                >
                                  <div
                                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                    style={{
                                      backgroundColor: isSelected ? getThemedPrimaryColor() : getThemedPrimaryWithOpacity(0.1),
                                    }}
                                  >
                                    {isSelected ? (
                                      <Check className="h-4 w-4" style={{ color: getContrastText(getThemedPrimaryColor()) }} />
                                    ) : (
                                      <User className="h-4 w-4" style={{ color: getThemedPrimaryColor() }} />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-foreground truncate">
                                      {customer.fullName}
                                    </p>
                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                      <Mail className="h-3 w-3" />
                                      <span className="truncate">{customer.email}</span>
                                    </div>
                                  </div>
                                </button>
                              );
                            })
                          )}
                        </div>
                        {pendingParticipants.length > 0 && (
                          <div className="pt-4 border-t">
                            <Button
                              type="button"
                              onClick={handleAddPendingParticipants}
                              className="w-full transition-all duration-300 hover:scale-105 font-semibold shadow-lg hover:shadow-xl"
                              style={{
                                backgroundColor: getThemedPrimaryColor(),
                                color: getContrastText(getThemedPrimaryColor()),
                                borderColor: getThemedPrimaryColor(),
                                boxShadow: `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}, 0 4px 6px -2px ${getThemedPrimaryWithOpacity(0.2)}`,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                                e.currentTarget.style.borderColor = getThemedSecondaryColor();
                                e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                                e.currentTarget.style.boxShadow = `0 25px 50px -12px ${getThemedPrimaryWithOpacity(0.5)}`;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = getThemedPrimaryColor();
                                e.currentTarget.style.borderColor = getThemedPrimaryColor();
                                e.currentTarget.style.color = getContrastText(getThemedPrimaryColor());
                                e.currentTarget.style.boxShadow = `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}, 0 4px 6px -2px ${getThemedPrimaryWithOpacity(0.2)}`;
                              }}
                            >
                              <UserPlus className="h-4 w-4 mr-2" />
                              Add {pendingParticipants.length} Participant{pendingParticipants.length !== 1 ? "s" : ""}
                            </Button>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
                {selectedParticipants.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No participants added yet
                  </p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedParticipants.map((participant) => (
                      <div
                        key={participant.id}
                        className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
                      >
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: getThemedPrimaryWithOpacity(0.1) }}
                        >
                          <User className="h-4 w-4" style={{ color: getThemedPrimaryColor() }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {participant.fullName}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {participant.email}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveParticipant(participant.id)}
                          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-sm border border-gray-200 dark:border-gray-700">
              <CardHeader className="px-4 pt-4 pb-2">
                <CardTitle className="text-sm">Before You Submit</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0">
                <div className="space-y-3 text-xs">
                  {hasKnowledgeBaseContent && (
                    <div>
                      <p className="text-gray-600 dark:text-gray-400 mb-2">
                        Check our help articles for quick answers.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleGoToKnowledgeBase}
                        className="w-full transition-all duration-200"
                        style={{
                          borderColor: getThemedSecondaryColor(),
                          color: getThemedSecondaryColor(),
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                          e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = getThemedSecondaryColor();
                        }}
                      >
                        Browse Knowledge Base
                      </Button>
                    </div>
                  )}
                  <div className={hasKnowledgeBaseContent ? "border-t border-gray-200 dark:border-gray-700 pt-3" : ""}>
                    <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Tips</p>
                    <ul className="text-gray-500 dark:text-gray-400 space-y-0.5">
                      <li>• Be specific about the issue</li>
                      <li>• Include error messages if any</li>
                      <li>• Attach screenshots if helpful</li>
                    </ul>
                  </div>
                  {organizationInfo?.portalSettings?.emailSubmission?.isEnabled && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                      <p className="text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        Or submit via email:
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            organizationInfo.portalSettings.emailSubmission?.emailAddress || ""
                          );
                          showToast.success("Copied!", "Email address copied to clipboard");
                        }}
                        className="w-full bg-gray-50 dark:bg-gray-800 rounded p-2 flex items-center justify-between gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                        title="Click to copy"
                      >
                        <code
                          className="text-xs font-mono break-all text-left"
                          style={{ color: getThemedPrimaryColor() }}
                        >
                          {organizationInfo.portalSettings.emailSubmission.emailAddress}
                        </code>
                        <Copy className="h-3 w-3 flex-shrink-0 text-gray-400" />
                      </button>
                      <p className="text-gray-500 dark:text-gray-400 mt-2">
                        Use a clear subject line and include details in the body. A ticket will be created automatically.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
